// Re-export API types with cleaner names
import type {
  employee_absences_AbsenceResponse,
  employee_absences_AbsenceListResponse,
  employee_absences_CreateAbsenceRequest,
  employee_absences_UpdateAbsenceRequest,
  employee_absences_UpdateRecurringAbsenceRequest,
  employee_absences_EmployeeBrief,
  organisations_AbsenceSettingsResponse,
} from '@/api';

export type EmployeeAbsence = employee_absences_AbsenceResponse;
export type EmployeeAbsenceList = employee_absences_AbsenceListResponse;
export type CreateEmployeeAbsenceRequest = employee_absences_CreateAbsenceRequest;
export type UpdateEmployeeAbsenceRequest = employee_absences_UpdateAbsenceRequest;
export type UpdateRecurringAbsenceRequest = employee_absences_UpdateRecurringAbsenceRequest;
export type EmployeeBrief = employee_absences_EmployeeBrief;

// Absence type enum
export const AbsenceType = {
  VACATION: 'VACATION',
  SICK: 'SICK',
  PERSONAL: 'PERSONAL',
  TRAINING: 'TRAINING',
  OTHER: 'OTHER',
  ACCIDENT: 'ACCIDENT',
  COMP_TIME: 'COMP_TIME',
  // A child's illness (CH Art. 36 para. 3 ArG, DE section 45 SGB V). A report
  // type, counted in calendar days like SICK and ACCIDENT.
  CHILD_SICK: 'CHILD_SICK',
} as const;
export type AbsenceTypeValue = (typeof AbsenceType)[keyof typeof AbsenceType];

// Derived certificate state of a reported absence (set by the backend, never stored).
export const CertificateStatus = {
  NOT_DUE: 'NOT_DUE',
  DUE: 'DUE',
  PROVIDED: 'PROVIDED',
  EAU_RETRIEVED: 'EAU_RETRIEVED',
  NOT_REQUIRED: 'NOT_REQUIRED',
} as const;
export type CertificateStatusValue = (typeof CertificateStatus)[keyof typeof CertificateStatus];

// How the organisation collects doctor's certificates (absence settings).
export const CertificateMode = {
  UPLOAD: 'UPLOAD',
  EAU: 'EAU',
} as const;
export type CertificateModeValue = (typeof CertificateMode)[keyof typeof CertificateMode];

// Organisation absence settings (GET /organisations/{id}/absence-settings), always the full resolved shape.
export type AbsenceSettings = organisations_AbsenceSettingsResponse;

/**
 * The PUT body of the absence settings with the certificate mode as a plain
 * string-literal union. The generated request DTO types the mode as a nominal
 * enum with the SAME two values; `useUpdateAbsenceSettings` maps it.
 */
export interface AbsenceSettingsInput {
  certificateMode: CertificateModeValue;
  certificateDueDay: {
    SICK: number;
    ACCIDENT: number;
    CHILD_SICK: number;
  };
  certificateRetentionMonths: number;
}

// Approval status enum
export const ApprovalStatus = {
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
} as const;
export type ApprovalStatusValue = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];

// Recurrence type enum for absence patterns
export const AbsenceRecurrenceType = {
  WEEKLY: 'WEEKLY',
  BI_WEEKLY: 'BI_WEEKLY',
  MONTHLY: 'MONTHLY',
  CUSTOM: 'CUSTOM',
} as const;
export type AbsenceRecurrenceTypeValue = (typeof AbsenceRecurrenceType)[keyof typeof AbsenceRecurrenceType];

// Filter params interface
export interface EmployeeAbsenceListParams {
  employeeId?: string;
  absenceType?: AbsenceTypeValue;
  fromDate?: string; // YYYY-MM-DD
  toDate?: string; // YYYY-MM-DD
  limit?: number;
  offset?: number;
  // Report filters (#1767); each is sent only when set.
  approvalStatus?: ApprovalStatusValue;
  isSelfReport?: boolean;
  acknowledged?: boolean;
  certificateStatus?: CertificateStatusValue;
}

// Office certificate list of a reported absence (#1767): metadata only, no URL.
export type {
  employee_absences_AbsenceCertificateResponse as AbsenceCertificate,
  employee_absences_AbsenceCertificateListResponse as AbsenceCertificateList,
  employee_absences_AbsenceCertificateDownloadResponse as AbsenceCertificateDownload,
} from '@/api';

// `inline` opens the certificate in the browser (logged as a view), `attachment` downloads it.
export type CertificateDisposition = 'attachment' | 'inline';

// Pending (approval queue) params; `forMe` limits the queue to items routed to the caller.
export interface PendingAbsenceParams {
  limit?: number;
  offset?: number;
  forMe?: boolean;
}
